import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { JooiceTheme } from 'src/theme/theme';

interface IOpenWindow {
  url: string;
  target: string;
  features?: string;
  onSuccess?: () => void;
}

/**
 * Opens a URL in a new window/tab and handles popup blocker detection
 * @param url - The URL to open
 * @param target - The target attribute ('_blank', '_self', etc.)
 * @param features - Window features string, such as noopener
 * @returns The window object if successful, null if blocked
 */
export const safeWindowOpen = (url: string, target: string = '_blank', features?: string): Window | null => {
  // Store the original window.open function
  const popup = window.open(url, target, features);

  // Check if popup was blocked
  if (!popup || popup.closed || typeof popup.closed === 'undefined') return null;

  return popup;
};

/**
 * React hook that provides a function to safely open a URL in a new window/tab
 * with popup blocker detection and toast notification
 * @returns the new window object if success, null if blocked
 */
export const useSafeWindowOpen = () => {
  const theme = useTheme<JooiceTheme>();
  const { t } = useTranslation('commons');

  const openWindow = ({ url, target, features, onSuccess }: IOpenWindow): Window | null => {
    const popup = safeWindowOpen(url, target, features);

    if (!popup) {
      toast.info(t('pop-up-blocker'), {
        style: {
          backgroundColor: theme.palette.info.background,
          width: '400px',
        },
      });
      return null;
    }

    onSuccess?.();

    return popup;
  };

  return { openWindow };
};
